import { useEffect, useState } from "react";
import { Package, ShieldCheck, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FibiLoading } from "../components/FibiLoading";

export default function LaporanAlat() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (m = month) => {
    setLoading(true);
    api.get("/reports/tool-profit", { params: { month: m } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (isAdmin) load(); }, []);

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="laporan-alat-forbidden">
        <ShieldCheck className="w-10 h-10 text-rose-500" />
        <p className="text-muted-foreground text-sm">Hanya Admin Fasnet / Super Admin yang dapat melihat laporan profit.</p>
      </div>
    );
  }

  const produk = data?.per_produk || [];
  const orders = data?.orders || [];
  const profit = data?.profit ?? 0;

  return (
    <div className="space-y-5" data-testid="laporan-alat-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-emerald-500" /> Laporan Profit Order Alat
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Penjualan alat ke agen vs harga modal — hanya order berstatus Selesai.</p>
        </div>
        <Input type="month" value={month} onChange={(e) => { setMonth(e.target.value); load(e.target.value); }} className="w-44" data-testid="input-profit-month" />
      </div>

      {data && (
        <div className="flex items-center gap-2 flex-wrap text-xs font-mono" data-testid="profit-summary">
          <span className="px-2.5 py-1.5 rounded-lg bg-accent/60 border border-border" data-testid="profit-orders">{data.orders_count} order selesai</span>
          <span className="px-2.5 py-1.5 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400" data-testid="profit-penjualan">Penjualan: {rupiah(data.total_penjualan)}</span>
          <span className="px-2.5 py-1.5 rounded-lg bg-accent/60 border border-border" data-testid="profit-modal">Modal: {rupiah(data.total_modal)}</span>
          <span className="px-2.5 py-1.5 rounded-lg bg-violet-500/10 border border-violet-500/30 text-violet-400" data-testid="profit-ongkir">Ongkir: {rupiah(data.total_ongkir)}</span>
          <span className={`px-2.5 py-1.5 rounded-lg border font-bold ${profit >= 0 ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-500" : "bg-rose-500/10 border-rose-500/30 text-rose-400"}`} data-testid="profit-total">Profit: {rupiah(profit)}</span>
        </div>
      )}

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="profit-product-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Package className="w-4 h-4 text-[#F04E23]" /> Profit per Produk</CardTitle></CardHeader>
        <Table className="min-w-[520px]" data-testid="profit-product-table">
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead><TableHead>Produk</TableHead><TableHead className="text-right">Qty Terjual</TableHead>
              <TableHead className="text-right">Modal</TableHead><TableHead className="text-right">Penjualan</TableHead><TableHead className="text-right">Profit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="p-0"><FibiLoading text="FIBI sedang memuat data..." size={20} /></TableCell></TableRow>}
            {!loading && !produk.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="profit-empty">Belum ada order selesai di bulan ini</TableCell></TableRow>}
            {produk.map((p, idx) => (
              <TableRow key={p.name} data-testid={`profit-product-${idx}`}>
                <TableCell className="font-mono text-muted-foreground">{idx + 1}</TableCell>
                <TableCell className="font-semibold text-xs">{p.name}</TableCell>
                <TableCell className="text-right font-mono">{p.qty}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{rupiah(p.modal)}</TableCell>
                <TableCell className="text-right font-mono">{rupiah(p.penjualan)}</TableCell>
                <TableCell className={`text-right font-mono font-bold ${p.profit >= 0 ? "text-emerald-500" : "text-rose-400"}`}>{rupiah(p.profit)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="profit-order-card">
        <CardHeader><CardTitle className="text-base font-heading">Rincian per Order</CardTitle></CardHeader>
        <Table className="min-w-[640px]" data-testid="profit-order-table">
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead><TableHead>Agen</TableHead><TableHead>Tanggal</TableHead>
              <TableHead className="text-right">Penjualan</TableHead><TableHead className="text-right">Ongkir</TableHead>
              <TableHead className="text-right">Modal</TableHead><TableHead className="text-right">Profit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && !orders.length && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">-</TableCell></TableRow>}
            {orders.map((o) => (
              <TableRow key={o.id} data-testid={`profit-order-${o.id}`}>
                <TableCell className="font-mono text-[11px]">{o.invoice_no}</TableCell>
                <TableCell><div className="text-xs font-medium">{o.agent}</div><div className="text-[10px] font-mono text-muted-foreground">{o.agent_code || "-"}</div></TableCell>
                <TableCell className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">{fmtDateTime(o.created_at)}</TableCell>
                <TableCell className="text-right font-mono">{rupiah(o.penjualan)}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{rupiah(o.ongkir)}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{rupiah(o.modal)}</TableCell>
                <TableCell className={`text-right font-mono font-bold ${o.profit >= 0 ? "text-emerald-500" : "text-rose-400"}`}>{rupiah(o.profit)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
